import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Check, Leaf, Loader2, MapPin, Save, Sprout } from 'lucide-react';
import type { BloqueMapa, MapaLote } from '../data/mapasLotes';
import { supabase } from '../supabase';

type Props = { mapa: MapaLote; loteId: string | number; onDataChange?: () => void };
type BloqueRegistrado = { id: string; num: number; plant_count: number | null; conteo_resultado_payload?: any };

function fmtFecha(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString('es-CR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function centro(path: SVGPathElement | null) {
  if (!path) return { x: 0, y: 0 };
  const box = path.getBBox();
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

export default function MapaLoteInteractivo({ mapa, loteId, onDataChange }: Props) {
  const [seleccionado, setSeleccionado] = useState<BloqueMapa | null>(null);
  const [centros, setCentros] = useState<Record<number, { x: number; y: number }>>({});
  const [registrados, setRegistrados] = useState<Record<number, BloqueRegistrado>>({});
  const [valorEditado, setValorEditado] = useState('');
  const [lecturaEditada, setLecturaEditada] = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const confirmados = useMemo(() => mapa.bloques.filter(b => b.plantas != null).length, [mapa]);

  useEffect(() => {
    let activo = true;
    setCargando(true);
    supabase.from('emp_bloques').select('id,num,plant_count,conteo_resultado_payload').eq('lote_id', loteId).eq('activo', true).order('num')
      .then(({ data, error }) => {
        if (!activo) return;
        if (!error) setRegistrados(Object.fromEntries(((data ?? []) as BloqueRegistrado[]).map(b => [b.num, b])));
        setCargando(false);
      });
    return () => { activo = false; };
  }, [loteId]);

  useEffect(() => {
    if (!seleccionado) return;
    const manual = registrados[seleccionado.numero]?.plant_count;
    const lectura = registrados[seleccionado.numero]?.conteo_resultado_payload?.mapa_croquis_plantas;
    setValorEditado(String(manual ?? seleccionado.plantas ?? ''));
    setLecturaEditada(String(lectura ?? seleccionado.plantas ?? ''));
    setMensaje('');
  }, [seleccionado, registrados]);

  const lecturaCroquis = (b: BloqueMapa) => registrados[b.numero]?.conteo_resultado_payload?.mapa_croquis_plantas ?? b.plantas;
  const valorVisible = (b: BloqueMapa) => registrados[b.numero]?.plant_count ?? lecturaCroquis(b);
  const totalCalculado = mapa.bloques.reduce((s, b) => s + (valorVisible(b) ?? 0), 0);
  const diferencia = totalCalculado - mapa.totalPlantas;
  const diferencias = mapa.bloques.map(b => {
    const registro = registrados[b.numero];
    const manual = registro?.plant_count ?? null;
    const croquis = lecturaCroquis(b);
    const delta = manual != null && croquis != null ? manual - croquis : null;
    return { bloque: b, existe: Boolean(registro), manual, croquis, delta };
  }).filter(d => !d.existe || d.manual == null || d.delta !== 0);

  async function guardarCantidad() {
    if (!seleccionado) return;
    const cantidad = Number(valorEditado.replace(/[^0-9]/g, ''));
    if (!Number.isFinite(cantidad) || cantidad < 0) { setMensaje('Ingresá una cantidad válida.'); return; }
    const registro = registrados[seleccionado.numero];
    if (!registro) { setMensaje('Este bloque aún no existe en los datos manuales.'); return; }
    setGuardando(true); setMensaje('');
    const { data, error } = await supabase.from('emp_bloques')
      .update({ plant_count: cantidad }).eq('id', registro.id)
      .select('id,num,plant_count').single();
    setGuardando(false);
    if (error) { setMensaje(`No se pudo guardar: ${error.message}`); return; }
    setRegistrados(prev => ({ ...prev, [seleccionado.numero]: data as BloqueRegistrado }));
    setMensaje('Cantidad guardada.');
    onDataChange?.();
  }

  async function guardarLectura() {
    if (!seleccionado) return;
    const cantidad = Number(lecturaEditada.replace(/[^0-9]/g, ''));
    if (!Number.isFinite(cantidad) || cantidad < 0) { setMensaje('Ingresá una lectura válida.'); return; }
    const registro = registrados[seleccionado.numero];
    if (!registro) { setMensaje('El bloque debe existir en los datos manuales para guardar la lectura.'); return; }
    const payload = { ...(registro.conteo_resultado_payload ?? {}), mapa_croquis_plantas: cantidad };
    setGuardando(true); setMensaje('');
    const { data, error } = await supabase.from('emp_bloques')
      .update({ conteo_resultado_payload: payload }).eq('id', registro.id)
      .select('id,num,plant_count,conteo_resultado_payload').single();
    setGuardando(false);
    if (error) { setMensaje(`No se pudo guardar: ${error.message}`); return; }
    setRegistrados(prev => ({ ...prev, [seleccionado.numero]: data as BloqueRegistrado }));
    setMensaje('Lectura del croquis guardada.');
    onDataChange?.();
  }

  return (
    <div className="h-full min-h-0 grid lg:grid-cols-[minmax(0,1fr)_280px]" style={{ background: '#f4f7f2' }}>
      <div className="min-h-0 overflow-auto p-3 sm:p-5">
        <div className="h-full min-h-[520px] rounded-2xl overflow-hidden shadow-sm"
          style={{ background: '#fbfcf9', border: '1px solid #dce4da' }}>
          <svg viewBox={mapa.viewBox} className="w-full h-full" role="img"
            aria-label={`Mapa interactivo del lote ${mapa.lote}`} preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="field-bg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#f9fbf7" /><stop offset="1" stopColor="#eef4eb" />
              </linearGradient>
              <filter id="field-shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#16351f" floodOpacity=".12" />
              </filter>
            </defs>
            <rect width="1200" height="820" fill="url(#field-bg)" />
            <path d="M0 475 H1200" stroke="#d8c7a6" strokeWidth="18" opacity=".65" />
            <path d="M330 95 V480 M805 92 V480" stroke="#d8c7a6" strokeWidth="14" opacity=".55" />
            <g fill="none" stroke="#bdc9b9" strokeWidth="2" strokeDasharray="7 8" opacity=".8">
              <path d="M0 475 H1200" /><path d="M330 95 V480" /><path d="M805 92 V480" />
            </g>

            {mapa.bloques.map((bloque) => {
              const activo = seleccionado?.numero === bloque.numero;
              const c = centros[bloque.numero];
              const plantas = valorVisible(bloque);
              const comparacion = diferencias.find(d => d.bloque.numero === bloque.numero);
              const conDiferencia = Boolean(comparacion);
              return (
                <g key={bloque.numero} tabIndex={0} role="button"
                  aria-label={`Bloque ${bloque.numero}, ${plantas?.toLocaleString('es-CR') ?? 'plantas por confirmar'}`}
                  onClick={() => setSeleccionado(activo ? null : bloque)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSeleccionado(bloque); }}
                  style={{ cursor: 'pointer', outline: 'none' }}>
                  <path d={bloque.path}
                    ref={node => {
                      if (!node || centros[bloque.numero]) return;
                      const next = centro(node);
                      setCentros(prev => ({ ...prev, [bloque.numero]: next }));
                    }}
                    fill={activo ? '#2f855a' : conDiferencia ? '#fde2d7' : plantas == null ? '#f5d98b' : '#d8ebd5'}
                    stroke={activo ? '#174f34' : conDiferencia ? '#c95837' : '#63836a'} strokeWidth={activo || conDiferencia ? 5 : 2.5}
                    filter="url(#field-shadow)" vectorEffect="non-scaling-stroke" />
                  {c && <>
                    <text x={c.x} y={c.y - 5} textAnchor="middle" fill={activo ? '#fff' : '#23452c'}
                      fontSize="17" fontWeight="800">B{bloque.numero}</text>
                    <text x={c.x} y={c.y + 15} textAnchor="middle" fill={activo ? '#e8fff0' : '#54705a'}
                      fontSize="12" fontWeight="600">
                      {plantas?.toLocaleString('es-CR') ?? 'Por confirmar'}
                    </text>
                  </>}
                </g>
              );
            })}

            <g transform="translate(1005 95)">
              <rect width="160" height="126" rx="14" fill="#fff" stroke="#dce4da" />
              <text x="18" y="28" fill="#26472e" fontSize="14" fontWeight="800">REFERENCIAS</text>
              <circle cx="24" cy="52" r="7" fill="#d8ebd5" stroke="#63836a" />
              <text x="41" y="57" fill="#526458" fontSize="13">Dato confirmado</text>
              <circle cx="24" cy="78" r="7" fill="#f5d98b" stroke="#9d7a25" />
              <text x="41" y="83" fill="#526458" fontSize="13">Por confirmar</text>
              <circle cx="24" cy="104" r="7" fill="#fde2d7" stroke="#c95837" />
              <text x="41" y="109" fill="#526458" fontSize="13">Diferencia manual</text>
            </g>
          </svg>
        </div>
      </div>

      <aside className="p-4 sm:p-5 overflow-y-auto" style={{ background: '#ffffff', borderLeft: '1px solid #dce4da' }}>
        <div className="flex items-start gap-3 mb-5">
          <div className="p-2.5 rounded-xl" style={{ background: '#e5f2e2', color: '#28603b' }}><MapPin size={20} /></div>
          <div><p className="text-[11px] uppercase tracking-[.16em] font-bold" style={{ color: '#76907d' }}>{mapa.finca}</p>
            <h2 className="text-xl font-extrabold" style={{ color: '#193b25' }}>Lote {mapa.lote}</h2></div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-5">
          <Info icon={<CalendarDays size={14} />} label="Apertura" value={fmtFecha(mapa.apertura)} />
          <Info icon={<CalendarDays size={14} />} label="Cierre" value={fmtFecha(mapa.cierre)} />
          <Info icon={<Leaf size={14} />} label="Bloques" value={String(mapa.bloques.length)} />
          <Info icon={<Sprout size={14} />} label="Total croquis" value={mapa.totalPlantas.toLocaleString('es-CR')} />
        </div>

        <div className="rounded-xl p-3 mb-5" style={{ background: diferencia === 0 ? '#edf7eb' : '#fff6dc', border: `1px solid ${diferencia === 0 ? '#cfe3ca' : '#ead69b'}` }}>
          <div className="flex justify-between text-sm"><span style={{ color: '#65766b' }}>Suma de bloques</span><strong style={{ color: '#294332' }}>{totalCalculado.toLocaleString('es-CR')}</strong></div>
          <div className="flex justify-between text-xs mt-1"><span style={{ color: '#78887d' }}>Diferencia</span><strong style={{ color: diferencia === 0 ? '#287142' : '#946c0f' }}>{diferencia > 0 ? '+' : ''}{diferencia.toLocaleString('es-CR')}</strong></div>
        </div>

        {!cargando && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#78917e' }}>Diferencias por bloque</p>
              <span className="text-xs font-bold" style={{ color: diferencias.length ? '#ad4b30' : '#287142' }}>{diferencias.length}</span>
            </div>
            {diferencias.length === 0 ? (
              <div className="rounded-xl px-3 py-2.5 text-xs flex items-center gap-2" style={{ background: '#edf7eb', color: '#287142', border: '1px solid #cfe3ca' }}>
                <Check size={14} /> Todos los bloques coinciden con el croquis.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {diferencias.map(d => (
                  <button key={d.bloque.numero} onClick={() => setSeleccionado(d.bloque)}
                    className="w-full rounded-lg px-3 py-2 text-left"
                    style={{ background: seleccionado?.numero === d.bloque.numero ? '#f9ddd2' : '#fff5f0', border: '1px solid #efc7b8' }}>
                    <div className="flex items-center justify-between text-xs">
                      <strong style={{ color: '#713722' }}>Bloque {d.bloque.numero}</strong>
                      <strong style={{ color: '#ad4b30' }}>
                        {d.delta == null ? 'Sin dato manual' : `${d.delta > 0 ? '+' : ''}${d.delta.toLocaleString('es-CR')}`}
                      </strong>
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color: '#8d6658' }}>
                      Croquis {d.croquis?.toLocaleString('es-CR') ?? '—'} · Manual {d.manual?.toLocaleString('es-CR') ?? '—'}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {seleccionado ? (
          <div className="rounded-2xl p-4" style={{ background: '#f4f8f2', border: '1px solid #d4e3d1' }}>
            <p className="text-[11px] uppercase tracking-widest font-bold mb-1" style={{ color: '#78917e' }}>Selección</p>
            <h3 className="text-lg font-extrabold mb-4" style={{ color: '#214a2e' }}>Bloque {seleccionado.numero}</h3>
            <Dato label="Ciclo" value={seleccionado.ciclo ?? '—'} />
            <label className="block mt-3 text-[11px] uppercase tracking-wider font-bold" style={{ color: '#718076' }}>Lectura del croquis</label>
            <div className="flex gap-2 mt-1.5">
              <input value={lecturaEditada} inputMode="numeric" onChange={e => setLecturaEditada(e.target.value)}
                className="min-w-0 flex-1 rounded-lg px-3 py-2 text-sm font-bold"
                style={{ background: '#fff', border: '1px solid #bdcdbc', color: '#24442e' }} />
              <button onClick={guardarLectura} disabled={guardando || cargando}
                className="rounded-lg px-3 inline-flex items-center gap-1.5 text-xs font-bold"
                style={{ background: '#406a9b', color: '#fff', opacity: guardando || cargando ? .6 : 1 }}>
                <Save size={13} /> Lectura
              </button>
            </div>
            <label className="block mt-4 text-[11px] uppercase tracking-wider font-bold" style={{ color: '#718076' }}>Dato manual / vigente</label>
            <div className="flex gap-2 mt-1.5">
              <input value={valorEditado} inputMode="numeric"
                onChange={e => setValorEditado(e.target.value)}
                className="min-w-0 flex-1 rounded-lg px-3 py-2 text-sm font-bold"
                style={{ background: '#fff', border: '1px solid #bdcdbc', color: '#24442e' }} />
              <button onClick={guardarCantidad} disabled={guardando || cargando}
                className="rounded-lg px-3 inline-flex items-center gap-1.5 text-xs font-bold"
                style={{ background: '#2f855a', color: '#fff', opacity: guardando || cargando ? .6 : 1 }}>
                {guardando ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar
              </button>
            </div>
            {mensaje && <p className="mt-2 text-xs" style={{ color: mensaje.includes('guardada') ? '#287142' : '#946c0f' }}>{mensaje}</p>}
            {seleccionado.nota && <p className="mt-3 text-xs rounded-lg p-2" style={{ color: '#7b5b0b', background: '#fff5d7' }}>{seleccionado.nota}</p>}
          </div>
        ) : (
          <div className="rounded-2xl p-4 text-center" style={{ border: '1px dashed #bdcdbc', color: '#6b7f70' }}>
            <MapPin size={22} className="mx-auto mb-2" />
            <p className="text-sm font-semibold">Seleccioná un bloque</p>
            <p className="text-xs mt-1">Verás aquí sus datos operativos.</p>
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 text-xs" style={{ color: '#627468' }}>
          <Check size={14} style={{ color: '#368151' }} /> {confirmados} de {mapa.bloques.length} cantidades transcritas
        </div>
        <p className="mt-2 text-[11px] leading-relaxed" style={{ color: '#8a998e' }}>{mapa.fuente}. Vista esquemática, no georreferenciada.</p>
      </aside>
    </div>
  );
}

function Info({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return <div className="rounded-xl p-3" style={{ background: '#f6f8f5', border: '1px solid #e2e8df' }}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold" style={{ color: '#819087' }}>{icon}{label}</div>
    <p className="mt-1 text-sm font-bold" style={{ color: '#294332' }}>{value}</p>
  </div>;
}

function Dato({ label, value }: { label: string; value: string }) {
  return <div className="flex justify-between py-2 text-sm" style={{ borderBottom: '1px solid #dce8d9' }}>
    <span style={{ color: '#718076' }}>{label}</span><strong style={{ color: '#24442e' }}>{value}</strong>
  </div>;
}
